import axios from 'axios';
import { TicketListResponse, TicketFilters, DashboardStats, Ticket, TicketStatus, TicketPriority } from '../types/ticket';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class AdminTicketService {
  private getHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  async getDashboardStats(): Promise<DashboardStats> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/tickets/dashboard`,
        { headers: this.getHeaders() }
      );

      // Handle different response structures
      const data = response.data?.data || response.data || {};

      return {
        unassignedTickets: data.unassignedTickets ?? 0,
        myTickets: data.myTickets ?? 0,
        urgentTickets: data.urgentTickets ?? 0,
        totalTickets: data.totalTickets ?? 0,
        todayTickets: data.todayTickets ?? 0,
        resolvedThisWeek: data.resolvedThisWeek ?? 0
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // Return default values instead of throwing
      return {
        unassignedTickets: 0,
        myTickets: 0,
        urgentTickets: 0,
        totalTickets: 0,
        todayTickets: 0,
        resolvedThisWeek: 0
      };
    }
  }

  async getTickets(filters: TicketFilters & {
    unassigned?: boolean;
    assignedToMe?: boolean;
    today?: boolean;
    resolvedThisWeek?: boolean;
  } = {}): Promise<TicketListResponse> {
    try {
      const params = new URLSearchParams();

      if (filters.status) params.append('status', filters.status);
      if (filters.category) params.append('category', filters.category);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.assignedTo) params.append('assignedTo', filters.assignedTo);
      if (filters.search) params.append('search', filters.search);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());

      // Special filters from dashboard
      if (filters.unassigned) params.append('unassigned', 'true');
      if (filters.assignedToMe) params.append('assignedToMe', 'true');
      if (filters.today) params.append('today', 'true');
      if (filters.resolvedThisWeek) params.append('resolvedThisWeek', 'true');

      const response = await axios.get(
        `${API_BASE_URL}/admin/tickets?${params.toString()}`,
        { headers: this.getHeaders() }
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching tickets:', error);
      throw error;
    }
  }

  async getTicketById(ticketId: string): Promise<Ticket> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/tickets/${ticketId}`,
        { headers: this.getHeaders() }
      );
      return response.data.data.ticket || response.data.data;
    } catch (error) {
      console.error('Error fetching ticket:', error);
      throw error;
    }
  }

  async assignTicket(ticketId: string, adminId: string, notes?: string): Promise<void> {
    try {
      await axios.post(
        `${API_BASE_URL}/admin/tickets/${ticketId}/assign`,
        { adminId, notes },
        { headers: this.getHeaders() }
      );
    } catch (error) {
      console.error('Error assigning ticket:', error);
      throw error;
    }
  }

  async getAdminUsers(): Promise<Array<{ _id: string; name: string; email: string }>> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/tickets/admins`,
        { headers: this.getHeaders() }
      );
      return response.data.data.admins;
    } catch (error) {
      console.error('Error fetching admin users:', error);
      throw error;
    }
  }

  async getAgentReports(params?: { dateFrom?: string; dateTo?: string }): Promise<any[]> {
    const q = new URLSearchParams();
    if (params?.dateFrom) q.append('dateFrom', params.dateFrom);
    if (params?.dateTo) q.append('dateTo', params.dateTo);
    const response = await axios.get(
      `${API_BASE_URL}/admin/tickets/reports/agents?${q.toString()}`,
      { headers: this.getHeaders() }
    );
    return response.data.data || [];
  }

  async getAgentReportDetail(adminId: string, params?: { dateFrom?: string; dateTo?: string; interval?: 'day' | 'week' | 'month' }): Promise<{ timeSeries: any[]; categoryBreakdown: any[]; }> {
    const q = new URLSearchParams();
    if (params?.dateFrom) q.append('dateFrom', params.dateFrom);
    if (params?.dateTo) q.append('dateTo', params.dateTo);
    if (params?.interval) q.append('interval', params.interval);
    const response = await axios.get(
      `${API_BASE_URL}/admin/tickets/reports/agents/${adminId}?${q.toString()}`,
      { headers: this.getHeaders() }
    );
    return response.data.data || { timeSeries: [], categoryBreakdown: [] };
  }

  async getTicketMessages(ticketId: string): Promise<any[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/tickets/${ticketId}/messages`,
        { headers: this.getHeaders() }
      );
      return response.data.data.messages || [];
    } catch (error) {
      console.error('Error fetching ticket messages:', error);
      throw error;
    }
  }

  async sendMessage(ticketId: string, message: string, isInternal: boolean = false, attachments: string[] = []): Promise<void> {
    try {
      await axios.post(
        `${API_BASE_URL}/admin/tickets/${ticketId}/messages`,
        { message, isInternal, attachments },
        { headers: this.getHeaders() }
      );
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  async addTicketResponse(ticketId: string, message: string): Promise<void> {
    try {
      await axios.post(
        `${API_BASE_URL}/admin/tickets/${ticketId}/responses`,
        { message },
        { headers: this.getHeaders() }
      );
    } catch (error) {
      console.error('Error adding ticket response:', error);
      throw error;
    }
  }

  async changeTicketStatus(ticketId: string, status: string, notes?: string): Promise<any> {
    const response = await axios.patch(
      `${API_BASE_URL}/admin/tickets/${ticketId}/status`,
      { status, notes },
      { headers: this.getHeaders() }
    );
    return response.data.data.ticket;
  }

  async updateTicketCategory(ticketId: string, category: string, notes?: string): Promise<any> {
    const response = await axios.patch(
      `${API_BASE_URL}/admin/tickets/${ticketId}/category`,
      { category, notes },
      { headers: this.getHeaders() }
    );
    return response.data.data.ticket;
  }

  async updateTicketPriority(ticketId: string, priority: string, notes?: string): Promise<any> {
    const response = await axios.patch(
      `${API_BASE_URL}/admin/tickets/${ticketId}/priority`,
      { priority, notes },
      { headers: this.getHeaders() }
    );
    return response.data.data.ticket;
  }

  async getTicketHistory(ticketId: string, page: number = 1, limit: number = 20): Promise<{
    history: Array<{
      _id: string;
      action: string;
      actionLabel: string;
      changes: Array<{
        field: string;
        oldValue: any;
        newValue: any;
        fieldLabel: string;
      }>;
      userId: { _id: string; name: string; email: string } | null;
      timestamp: string;
      metadata: {
        ip?: string;
        route?: string;
      };
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/tickets/${ticketId}/history?page=${page}&limit=${limit}`,
        { headers: this.getHeaders() }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching ticket history:', error);
      return {
        history: [],
        pagination: { page: 1, limit: 20, total: 0, pages: 0 }
      };
    }
  }
}

const adminTicketService = new AdminTicketService();
export default adminTicketService; 